import { usePaneStore } from "../state/store-context";
import { FieldInput } from "./FieldInput";
import { LineItemGrid } from "./LineItemGrid";
import type { LineItemColumn } from "./LineItemGrid";
import {
  useSmartSkipIterator,
  jobTypeThresholdAsTypeFloors,
} from "../hooks/useSmartSkipIterator";

export function FieldPanel() {
  const fields = usePaneStore((s) => s.fields);
  const groups = usePaneStore((s) => s.groups);
  const lineItems = usePaneStore((s) => s.lineItems);
  const focusMode = usePaneStore((s) => s.focusMode);
  const toggleFocusMode = usePaneStore((s) => s.toggleFocusMode);
  const toggleGroupCollapsed = usePaneStore((s) => s.toggleGroupCollapsed);
  const verifyThreshold = usePaneStore((s) => s.verifyThreshold);

  // Group fields by groupId
  const fieldsByGroup = new Map<string, number[]>();
  fields.forEach((f, i) => {
    const list = fieldsByGroup.get(f.groupId) ?? [];
    list.push(i);
    fieldsByGroup.set(f.groupId, list);
  });

  // Phase 1.5 WU-4 (R-135) — "needs attention" counter must reflect the
  // smart-skip iterator's actual visit set (all fields whose effective
  // floor isn't satisfied), NOT the legacy per-field `needsAttention`
  // flag which was set only on the OCR-extracted subset.
  // F-046 — honor the step's re-verification policy (see StatusBar).
  const stepReverifyPolicy = usePaneStore((s) => s.stepReverifyPolicy);
  const typeFloors = jobTypeThresholdAsTypeFloors(fields, verifyThreshold);
  const visit = useSmartSkipIterator(fields, typeFloors, stepReverifyPolicy);
  const attentionCount = visit.visitOrder.length;
  const doneCount = fields.filter(
    (f) => f.validationStatus === "pass" || f.validationStatus === "overridden",
  ).length;

  return (
    <div className="field-panel">
      <div className="field-panel-header">
        <span className="field-panel-stats">
          {attentionCount > 0 && (
            <span className="attention-badge">{attentionCount} need attention</span>
          )}
          <span className="done-count">{doneCount}/{fields.length}</span>
        </span>
        <button
          className={`focus-toggle ${focusMode ? "active" : ""}`}
          onClick={toggleFocusMode}
          title="Focus mode — show only fields needing attention"
        >
          Focus
        </button>
      </div>

      {focusMode && (
        <div className="focus-mode-header">
          <span className="focus-mode-label">
            Focus Mode &mdash; {attentionCount} of {fields.length} fields need attention
          </span>
          <button className="focus-mode-exit" onClick={toggleFocusMode}>
            Show All
          </button>
        </div>
      )}

      <div className="field-panel-body">
        {groups.map((group) => {
          // Line item groups render as a grid
          if (group.type === "line_items") {
            const rows = lineItems[group.id] ?? [];
            // Derive columns from the first row's fields or from field definitions
            const groupFields = fields.filter((f) => f.groupId === group.id);
            const columns: LineItemColumn[] = groupFields.map((f) => ({
              name: f.name,
              label: f.label,
              dataType: f.dataType,
            }));

            return (
              <div key={group.id} className="field-group">
                <div
                  className="field-group-header"
                  onClick={() => group.isCollapsible && toggleGroupCollapsed(group.id)}
                >
                  <span className="field-group-name">{group.name}</span>
                  {group.isCollapsible && (
                    <span className="field-group-chevron">
                      {group.isCollapsed ? "▸" : "▾"}
                    </span>
                  )}
                </div>
                {!group.isCollapsed && (
                  <LineItemGrid
                    groupId={group.id}
                    groupName={group.name}
                    columns={columns}
                    rows={rows}
                  />
                )}
              </div>
            );
          }

          const indices = fieldsByGroup.get(group.id) ?? [];
          if (indices.length === 0) return null;

          // R-135 — focus-mode visible-fields filter uses the same smart-skip
          // visit set as the counter, so the operator sees exactly the fields
          // the counter reports.
          const visibleIndices = focusMode
            ? indices.filter((i) => {
                const f = fields[i];
                return f ? visit.isLowConf(f.name) : false;
              })
            : indices;

          if (visibleIndices.length === 0 && focusMode) return null;

          return (
            <div key={group.id} className="field-group">
              <div
                className="field-group-header"
                onClick={() => group.isCollapsible && toggleGroupCollapsed(group.id)}
              >
                <span className="field-group-name">{group.name}</span>
                {group.isCollapsible && (
                  <span className="field-group-chevron">
                    {group.isCollapsed ? "▸" : "▾"}
                  </span>
                )}
              </div>
              {!group.isCollapsed && (
                <div
                  className="field-group-body"
                  style={{
                    gridTemplateColumns: `repeat(${group.layoutColumns}, 1fr)`,
                  }}
                >
                  {visibleIndices.map((fieldIndex) => (
                    <FieldInput key={fields[fieldIndex]!.id} fieldIndex={fieldIndex} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        .field-panel {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: var(--color-bg-secondary);
          border-left: 1px solid var(--color-border);
        }
        .field-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-2) var(--space-3);
          border-bottom: 1px solid var(--color-border);
          font-size: 0.75rem;
        }
        .field-panel-stats { display: flex; gap: var(--space-3); align-items: center; }
        .attention-badge {
          color: var(--color-warning);
          font-weight: 600;
        }
        .done-count { color: var(--color-text-muted); }
        .focus-toggle {
          font-size: 0.7rem;
          padding: 2px 8px;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          background: transparent;
          color: var(--color-text-secondary);
          cursor: pointer;
        }
        .focus-toggle.active {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }
        .field-panel-body {
          flex: 1;
          overflow-y: auto;
          padding: var(--space-2);
        }
        .field-group { margin-bottom: var(--space-2); }
        .field-group-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-1) var(--space-2);
          font-size: 0.7rem;
          font-weight: 600;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          cursor: pointer;
          user-select: none;
        }
        .field-group-chevron { font-size: 0.8rem; }
        .focus-mode-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-1) var(--space-3);
          background: color-mix(in srgb, var(--color-primary) 8%, transparent);
          border-bottom: 1px solid var(--color-border);
          font-size: 0.7rem;
        }
        .focus-mode-label {
          color: var(--color-primary);
          font-weight: 600;
        }
        .focus-mode-exit {
          font-size: 0.65rem;
          padding: 1px 6px;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          background: transparent;
          color: var(--color-text-secondary);
          cursor: pointer;
          text-decoration: underline;
        }
        .focus-mode-exit:hover {
          background: var(--color-bg-tertiary);
        }
        .field-group-body {
          display: grid;
          gap: 1px;
        }
        .field-row {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: 2px var(--space-2);
          border-left: 2px solid transparent;
        }
        .field-row.field-active {
          background: var(--color-bg-tertiary);
          border-left-color: var(--color-primary);
        }
        .field-row.status-fail { border-left-color: var(--color-error); }
        .field-row.status-overridden { border-left-color: #d97706; }
        .field-row.status-pass { border-left-color: var(--color-success); }
        .field-label {
          flex: 0 0 90px;
          font-size: 0.75rem;
          color: var(--color-text-secondary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .field-required { color: var(--color-error); margin-left: 2px; }
        .field-input-wrap {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .field-input {
          width: 100%;
          padding: 2px 6px;
          font-family: var(--font-mono);
          font-size: 0.8rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          background: var(--color-bg-primary);
          color: var(--color-text-primary);
          outline: none;
        }
        .field-input:focus {
          border-color: var(--color-primary);
        }
        .field-input.confidence-low { color: var(--color-confidence-low); }
        .field-input.confidence-medium { color: var(--color-confidence-medium); }
        .field-error {
          color: var(--color-error);
          font-weight: 700;
          font-size: 0.75rem;
          cursor: help;
        }
        .field-override-icon {
          color: #d97706;
          font-size: 0.7rem;
          cursor: help;
        }
        .field-confidence {
          font-size: 0.6rem;
          color: var(--color-text-muted);
          min-width: 20px;
          text-align: right;
        }
        .field-confidence.confidence-high { color: var(--color-confidence-high); }
        .field-confidence.confidence-medium { color: var(--color-confidence-medium); }
        .field-confidence.confidence-low { color: var(--color-confidence-low); }
        /* F-089 — dim only the LABEL of an auto-verified row; the VALUE stays
           at normal text color (an instrument readout, not a placeholder).
           Muted values are reserved for derived/read-only inputs. */
        .field-auto-verified .field-label {
          color: var(--color-text-muted);
          opacity: 0.7;
        }
        .field-auto-check {
          color: var(--color-success);
          font-size: 0.65rem;
          margin-right: 3px;
        }
        .handwriting-indicator {
          margin-left: 4px;
          font-size: 0.75em;
          opacity: 0.7;
          cursor: help;
        }
      `}</style>
    </div>
  );
}
